import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import signatureImg from "../assets/saam-signature.png";

function addPageBorder(doc) {
  doc.setLineWidth(0.4);
  doc.rect(10, 10, 190, 277);
}

function addFooter(doc, pageNo) {
  try {
    // Signature Right Side
    doc.addImage(
      signatureImg,
      "PNG",
      150, // X
      255, // Y
      38,  // Width
      20   // Height
    );
  } catch (e) {
    console.log(e);
  }

  doc.setFont("times", "bold");
  doc.setFontSize(7);

  doc.text(
    "Authorised Signatory - SAAM",
    148,
    278
  );

  // Page Number Center
  doc.setFont("times", "normal");
  doc.setFontSize(8);

  doc.text(
    `Page ${__pageNo}`,
    105, // center of A4
    282,
    { align: "center" }
  );
}
let __pageNo = 1;

function compactValue(value) {
  if (
    value === null ||
    value === undefined ||
    typeof value === "object" ||
    typeof value === "boolean"
  ) {
    return "";
  }

  return String(value)
    .replace(/\s+/g, " ")
    .replace(/\s+,/g, ",")
    .trim();
}

function firstValue(...values) {
  for (const value of values.flat(Infinity)) {
    const text = compactValue(value);
    if (
      text &&
      !["undefined", "null", "n/a", "na", "-", "—"].includes(
        text.toLowerCase()
      )
    ) {
      return text;
    }
  }
  return "";
}

function numericValue(...values) {
  const value = firstValue(...values);
  const number = Number(String(value).replace(/[^0-9.]/g, ""));
  return Number.isFinite(number) ? number : 0;
}

function addMissingLocationPart(parts, completeNeedle, label, value) {
  const safeValue = compactValue(value);
  if (!safeValue) return;
  if (completeNeedle && completeNeedle.includes(safeValue.toLowerCase())) return;
  parts.push(label ? `${label}: ${safeValue}` : safeValue);
}

function normalizeLOAData(input = {}) {
  const project = input.project || {};
  const parties = input.parties || {};
  const contractor = parties.contractor || {};
  const beneficiary = parties.beneficiary || {};
  const pmc = parties.pmc || {};

  const pacsName = firstValue(
    input.pacsName,
    input.societyName,
    input.beneficiaryName,
    project.pacsName,
    beneficiary.legalName
  );

  const contractorName = firstValue(
    input.contractorLegalName,
    input.contractorName,
    input.awardedVendorName,
    input.recipientName,
    contractor.legalName
  );

  const contractorAddress = firstValue(
    input.contractorRegisteredOffice,
    input.contractorAddress,
    input.awardedVendorAddress,
    input.recipientAddress,
    contractor.registeredOffice
  );

  const village = firstValue(input.village, project.village);
  const taluka = firstValue(input.taluka, project.taluka);
  const district = firstValue(input.district, project.district);
  const state = firstValue(input.state, project.state);
  const pinCode = firstValue(input.pinCode, input.pincode, project.pinCode);

  const rawSiteAddress = firstValue(
    input.siteAddress,
    input.projectAddress,
    input.receivingLocation,
    input.projectLocation,
    project.siteAddress,
    beneficiary.projectSite
  );

  const locationParts = [];
  const rawNeedle = rawSiteAddress.toLowerCase();
  if (rawSiteAddress) locationParts.push(rawSiteAddress);
  addMissingLocationPart(locationParts, rawNeedle, "At/Post", village);
  addMissingLocationPart(locationParts, rawNeedle, "Taluka", taluka);
  addMissingLocationPart(locationParts, rawNeedle, "District", district);
  addMissingLocationPart(locationParts, rawNeedle, "", state);
  addMissingLocationPart(locationParts, rawNeedle, "PIN", pinCode);

  const projectCapacityMt = numericValue(
    input.projectCapacityMt,
    input.capacityMt,
    project.capacityMt,
    input.projectCapacity
  );

  const normalized = {
    ...input,
    loaDate: firstValue(input.loaDate, input.loaGenerateDate),
    pacsName,
    societyName: pacsName,
    beneficiaryName: pacsName,
    contractorName,
    contractorLegalName: contractorName,
    contractorAddress,
    contractorRegisteredOffice: contractorAddress,
    contractorVendorId: firstValue(
      input.contractorVendorId,
      contractor.vendorId
    ),
    contractorCompanyType: firstValue(
      input.contractorCompanyType,
      contractor.companyType
    ),
    contractorAuthorizedSignatory: firstValue(
      input.contractorAuthorizedSignatory,
      contractor.authorisedSignatory,
      contractor.authorizedSignatory
    ),
    contractorDesignation: firstValue(
      input.contractorDesignation,
      contractor.designation,
      "Authorised Signatory"
    ),
    contractorGstin: firstValue(
      input.contractorGstin,
      input.contractorGSTIN,
      contractor.gstin,
      contractor.gst
    ),
    contractorPan: firstValue(
      input.contractorPan,
      contractor.pan
    ),
    contractorEmail: firstValue(
      input.contractorEmail,
      contractor.email
    ),
    contractorMobile: firstValue(
      input.contractorMobile,
      contractor.mobile,
      contractor.phone
    ),
    contractorCin: firstValue(input.contractorCin, contractor.cin),
    contractorUdyam: firstValue(input.contractorUdyam, contractor.udyam),
    contractorClass: firstValue(
      input.contractorClass,
      contractor.contractorClass
    ),
    village,
    taluka,
    district,
    state,
    pinCode,
    projectLocation: village || rawSiteAddress,
    projectAddress: locationParts.join(", "),
    fullProjectLocation: locationParts.join(", "),
    receivingLocation: locationParts.join(", "),
    projectCapacityMt,
    capacityMt: projectCapacityMt,
    projectCapacity: projectCapacityMt ? `${projectCapacityMt} MT` : "",
    workDurationDays: numericValue(input.workDurationDays),
    defectLiabilityMonths:
      numericValue(input.defectLiabilityMonths) || 12,
    performanceSecurity:
      numericValue(input.performanceSecurity) || 250000,
    liquidatedDamagesPerWeekPercent:
      numericValue(input.liquidatedDamagesPerWeekPercent) || 0.5,
    liquidatedDamagesCapPercent:
      numericValue(input.liquidatedDamagesCapPercent) || 10,
    defaultCurePeriodDays:
      numericValue(input.defaultCurePeriodDays) || 7,
    claimNoticeDays:
      numericValue(input.claimNoticeDays) || 7,
    jurisdiction: firstValue(
      input.jurisdiction,
      "Palanpur, District Banaskantha, Gujarat"
    ),
    l1Amount: numericValue(
      input.l1Amount,
      input.acceptedContractValueExcludingGst
    ),
    gstRate: numericValue(input.gstRate),
    gstAmount: numericValue(input.gstAmount),
    totalAmount: numericValue(
      input.totalAmount,
      input.acceptedContractValueIncludingGst
    ),
    pmcName: firstValue(
      input.pmcLegalName,
      input.pmcName,
      pmc.legalName,
      "Shine Agri Allied & Marketing Multistate Cooperative Society Limited"
    ),
    pmcAddress: firstValue(
      input.pmcRegisteredOffice,
      input.pmcAddress,
      pmc.registeredOffice,
      "48, 1st Amar Arcade, Nilpur, Taluka Dantiwada, District Banaskantha, Gujarat - 385505"
    ),
    pmcRegistrationNo: firstValue(
      input.pmcRegistrationNo,
      pmc.registrationNo,
      "MSCS/CR/1572/2024"
    ),
  };

  if (!normalized.gstAmount && normalized.l1Amount && normalized.gstRate) {
    normalized.gstAmount = Math.round(
      (normalized.l1Amount * normalized.gstRate) / 100
    );
  }

  if (!normalized.totalAmount && normalized.l1Amount) {
    normalized.totalAmount = normalized.l1Amount + normalized.gstAmount;
  }

  const required = [
    ["LOA date", normalized.loaDate],
    ["PACS / Society legal name", normalized.pacsName],
    ["project capacity", normalized.projectCapacity],
    ["project village", normalized.village],
    ["project taluka", normalized.taluka],
    ["project district", normalized.district],
    ["project state", normalized.state],
    ["complete project location", normalized.fullProjectLocation],
    ["contractor legal name", normalized.contractorName],
    ["contractor registered-office address", normalized.contractorAddress],
    ["accepted contract value", normalized.l1Amount],
    ["work duration", normalized.workDurationDays],
  ];

  const errors = required
    .filter(([, value]) => !value)
    .map(([label]) => `Missing ${label}`);

  if (normalized.pacsName.toLowerCase() === normalized.contractorName.toLowerCase()) {
    errors.push("PACS and Contractor names are identical");
  }

  if (
    normalized.projectAddress.toLowerCase() ===
    normalized.contractorAddress.toLowerCase()
  ) {
    errors.push("Project site and Contractor registered-office addresses are identical");
  }

  if (errors.length) {
    throw new Error(
      "LOA master data is incomplete or mixed: " + errors.join("; ")
    );
  }

  return normalized;
}

function cleanPdfText(text) {
  return String(text || "")
    .replace(/₹/g, "Rs. ")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/–/g, "-")
    .replace(/—/g, "-");
}

function addWrappedText(doc, text, x, y, maxWidth, lineHeight = 4) {
  const safeText = cleanPdfText(text);
  const safeWidth = Math.min(maxWidth || 155, 155);

  const lines = doc.splitTextToSize(safeText, safeWidth);

  lines.forEach((line) => {
    doc.text(line, x, y);
    y += lineHeight;
  });

  return y;
}
function addPage1(doc, data) {
  addPageBorder(doc);

  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.text(`Date: ${data.loaDate || ""}`, 165, y);

  y += 14;

  doc.text(`The ${data.contractorDesignation}`, 18, y);
  y += 5;

  doc.text(data.contractorName, 18, y);
  y += 5;

  doc.setFont("times", "normal");
  const addressLines = doc.splitTextToSize(
    data.contractorAddress,
    90
  );
  doc.text(addressLines, 18, y);
  y += addressLines.length * 5 + 8;

  doc.setFont("times", "bold");
  doc.text("Dear Sir,", 18, y);
  y += 6;

  doc.setFontSize(11);
  doc.text("LETTER OF AWARD (LOA)", 105, y, { align: "center" });
  y += 4;

  doc.setFontSize(10);
  const subject =
    `For Establishment of ${data.projectCapacity} Scientific Grain Storage Structure for ` +
    `${data.pacsName}, at ${data.fullProjectLocation}.`;

  y = addWrappedText(doc, subject, 18, y, 174);
  y += 5;

  doc.setLineWidth(0.3);
  doc.line(18, y, 192, y);
  y += 5;

  doc.setFont("times", "normal");

  y = addWrappedText(
    doc,
    `We are pleased to inform you that after evaluation of all bids received under the tender process, your firm ${
      data.contractorName
    } has been selected as the Lowest Responsive Bidder (L1) for execution of the turnkey construction works for the ${
      data.projectCapacity
    } Scientific Grain Storage Structure for ${
      data.pacsName
    }, at ${data.fullProjectLocation}.`,
    18,
    y,
    174
  );

  y += 5;

  y = addWrappedText(
    doc,
    `Accordingly, ${
      data.contractorName
    } is hereby appointed as the Turnkey Contractor through this Letter of Award for the captioned work, subject to the terms, conditions, specifications, drawings, schedules, Bill of Quantities, quality standards and obligations contained herein.`,
    18,
    y,
    174
  );

  y += 6;

  doc.setFont("times", "bold");
  doc.text("Contractor:", 18, y);

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    `${data.contractorName}, having its registered office at ${data.contractorAddress}.`,
    52,
    y,
    138
  );

  y += 4;

  doc.setFont("times", "bold");
  doc.text("PMC / SAAM:", 18, y);

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    `${data.pmcName} (Registration No. ${data.pmcRegistrationNo}), having its registered office at ${data.pmcAddress}, acting as Project Management Consultant, authorised implementing, supervisory, monitoring and certifying authority for the Project.`,
    52,
    y,
    138
  );

  y += 5;

  doc.setFont("times", "bold");
  doc.text("Electronic Acceptance:", 18, y);

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    `This Letter of Award may be generated, issued, accepted, signed, uploaded and stored electronically through the SAAM Tender Management Portal. Any signed upload, digital acknowledgement, portal approval or system log shall be deemed valid and binding acceptance by the Contractor.`,
    62,
    y,
    128
  );

  y += 4;

  doc.setLineWidth(0.3);
  doc.line(18, y, 192, y);
  y += 5;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("I. SCOPE OF WORK", 18, y);
  y += 5;

  doc.setFontSize(10);
  doc.text("1. Mobilization", 18, y);
  y += 4;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    `The Contractor shall mobilize complete manpower, machinery, construction equipment, raw materials, shuttering materials, quality control arrangements, safety infrastructure, site office, supervisory staff, labour force and all tools & tackles required for execution of the Project within 7 (Seven) calendar days from the date of issuance of this Letter of Award.`,
    18,
    y,
    174
  );

  y += 5;

  addWrappedText(
    doc,
    `Failure to mobilize within the prescribed period shall constitute material breach of contract and SAAM shall be entitled to impose penalties, recover losses, withhold payments, invoke security provisions and/or terminate this Letter of Award without any liability.`,
    18,
    y,
    174
  );

  addFooter(doc, 1);
}


function addPage2(doc, data) {
  addPageBorder(doc);

  let y = 25;

  doc.setFont("times", "bold");
  doc.setFontSize(10);

  doc.text("The manpower must mandatorily include:", 18, y);

  y += 6;

  doc.setFont("times", "normal");

  const manpowerList = [
    "Project Manager",
    "Civil Engineer",
    "QA/QC Manager",
    "Safety Supervisor",
    "Store Keeper",
    "Skilled & Unskilled Labour",
  ];

  manpowerList.forEach((item) => {
    doc.circle(20, y - 1, 0.8, "F");
    doc.text(item, 25, y);
    y += 6;
  });

  y += 3;

  const manpowerText =
    "All mobilized staff must remain deployed continuously until project completion to ensure quality and timely execution.";

  y = addWrappedText(
    doc,
    manpowerText,
    18,
    y,
    170
  );

  y += 6;

  doc.setLineWidth(0.3);
  doc.line(18, y, 192, y);

  y += 12;

  doc.setFont("times", "bold");
  doc.setFontSize(11);

  doc.text("2. Job Completion Time", 18, y);

  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const completionText =
    `All works shall be completed within ${data.workDurationDays || 180} days from the date of this Letter of Award, including:`;

  y = addWrappedText(
    doc,
    completionText,
    18,
    y,
    170
  );

  y += 5;

  const works = [
    "Civil Works",
    "Foundation",
    "Plinth",
    "Superstructure",
    "PEB Shed Erection",
    "Flooring",
    "Painting & Finishing",
    "Electrification",
    "Final Handover",
  ];

  works.forEach((item) => {
    doc.circle(20, y - 1, 0.8, "F");
    doc.text(item, 25, y);
    y += 6;
  });

  y += 3;

  y = addWrappedText(
    doc,
    "Coordination with PMC for timely installation of structural components and equipment must also be ensured within the project duration.",
    18,
    y,
    170
  );

  y += 5;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "Time shall be treated as the essence of this Contract.",
    18,
    y,
    170
  );

  doc.setFont("times", "normal");

  y += 4;

  y = addWrappedText(
    doc,
    "Any delay beyond the approved completion period shall attract Liquidated Damages (LD), recovery of losses, withholding of payments, recovery from Security Deposit and other contractual remedies available to SAAM.",
    18,
    y,
    170
  );

  y += 4;

  y = addWrappedText(
    doc,
    "No extension of time shall be granted unless specifically approved in writing by SAAM.",
    18,
    y,
    170
  );

  addFooter(doc, 2);
}
function addTimelineBar(doc, x, y, w = 14) {
  doc.setFillColor(0, 0, 0);
  doc.rect(x, y, w, 4, "F");
}

function addPage3(doc, data) {
  addPageBorder(doc);
  let y = 22;
  const totalDays = Number(data.workDurationDays);
  const totalWeeks = Math.ceil(totalDays / 7);
  const checkpoints = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 16, 20]
    .filter((week) => week < totalWeeks);
  checkpoints.push(totalWeeks);

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text(
    `Project Activity Timeline (${totalDays} Days Completion)`,
    15,
    y
  );

  y += 6;

  const weeks = ["Week No.", ...checkpoints.map(String)];
  const days = [
    "Cumulative Days",
    ...checkpoints.map((week) => String(Math.min(week * 7, totalDays))),
  ];

  const x0 = 15;
  const colW = [45, ...Array(checkpoints.length).fill(135 / checkpoints.length)];
  const rowH = 9;

  [weeks, days].forEach((row, r) => {
    let x = x0;
    row.forEach((cell, i) => {
      doc.rect(x, y + r * rowH, colW[i], rowH);
      doc.text(String(cell), x + 2, y + r * rowH + 6);
      x += colW[i];
    });
  });

  y += 32;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  y = addWrappedText(
    doc,
    `Activity Schedule (For ${data.projectCapacity} Scientific Godown - ${data.pacsName})`,
    15,
    y,
    178,
    4
  );

  y += 2;

  const tableX = 15;
  const tableY = y;
  const activityW = 65;
  const phaseW = 19;
  const hHead = 22;
  const hRow = 17;

  const phaseSize = Math.ceil(totalWeeks / 6);
  const headers = [
    "Activities",
    ...Array.from({ length: 6 }, (_, index) => {
      const start = index * phaseSize + 1;
      const end = Math.min((index + 1) * phaseSize, totalWeeks);
      return start === end ? `Week ${start}` : `${start}-${end} Week`;
    }),
  ];

  let x = tableX;
  doc.setFontSize(9);
  headers.forEach((h, i) => {
    const w = i === 0 ? activityW : phaseW;
    doc.rect(x, y, w, hHead);
    const lines = doc.splitTextToSize(h, w - 4);
    doc.text(lines, x + 2, y + 7);
    x += w;
  });

  y += hHead;

  const rows = [
    { name: "Mobilisation of Contractor", bars: [[1, 14]] },
    { name: "Digging of Borewell / Water Source & Temporary Power Setup", bars: [[1, 8]] },
    { name: "Foundation & Casings (10 Footings – GPS & Photo Mandatory)", bars: [[1, 18], [2, 9]] },
    { name: "Plinth Beams & Compound Wall", bars: [[2, 15], [3, 9]] },
    { name: "Erection of Steel / PEB Sheds", bars: [[3, 12], [4, 15]] },
    { name: "Construction of Brick Works", bars: [[2, 9], [3, 12], [4, 9]] },
    { name: "Internal Plastering / External Plastering", bars: [[3, 12], [4, 15]] },
    { name: "Painting & Finishing Works", bars: [[4, 15], [5, 9], [6, 9]] },
  ];

  rows.forEach((row) => {
    let x = tableX;
    doc.rect(x, y, activityW, hRow);
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.text(doc.splitTextToSize(row.name, activityW - 4), x + 2, y + 6);
    x += activityW;

    for (let i = 1; i <= 6; i++) {
      doc.rect(x, y, phaseW, hRow);
      const bar = row.bars.find((b) => b[0] === i);
      if (bar) addTimelineBar(doc, x + 2, y + 6, bar[1]);
      x += phaseW;
    }

    y += hRow;
  });

  addFooter(doc, 3);
}

function addPage4(doc, data) {
  addPageBorder(doc);
  let y = 22;

  const tableX = 15;
  const activityW = 65;
  const phaseW = 19;
  const hRow = 22;

  const rows = [
    { name: "Grade Slab, Roads & Drains (With Curing, Consolidation)", bars: [[3, 9], [4, 9], [5, 9]] },
    { name: "Order Placement for Machinery & Agriculture Equipment", bars: [[1, 9], [2, 9]] },
    { name: "Machinery & Equipment Reaching Site", bars: [[4, 9], [5, 9]] },
    { name: "Machinery Foundation / Erection (Control Panels, DG Set Etc.)", bars: [[5, 12], [6, 9]] },
    { name: "Internal & External Lighting / Wiring", bars: [[5, 12], [6, 9]] },
    { name: "Trial Run & Handover", bars: [[6, 9]] },
  ];

  rows.forEach((row) => {
    let x = tableX;

    doc.rect(x, y, activityW, hRow);
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.text(doc.splitTextToSize(row.name, activityW - 4), x + 2, y + 6);
    x += activityW;

    for (let i = 1; i <= 6; i++) {
      doc.rect(x, y, phaseW, hRow);
      const bar = row.bars.find((b) => b[0] === i);
      if (bar) addTimelineBar(doc, x + 2, y + 7, bar[1]);
      x += phaseW;
    }

    y += hRow;
  });

  y += 12;

  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.text("NOTE:", 15, y);
  y += 4;

  doc.setFont("times", "normal");
  const notes = [
    "Contractor must strictly follow the above activity schedule.",
    "Any deviation must be approved in writing by PMC.",
    "Weekly progress photos with GPS, date & time stamp must be uploaded on portal.",
    "Delay beyond approved timeline will attract penalties and may lead to termination.",
  ];

  notes.forEach((n) => {
    doc.circle(17, y - 1, 0.7, "F");
    y = addWrappedText(doc, n, 21, y, 165, 5);
    y += 2;
  });

  y += 5;
  doc.line(15, y, 195, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("3. Guarantee", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    `Contractor shall provide a ${data.defectLiabilityMonths}-month Defect Liability Guarantee for all civil, steel, PEB, foundation, flooring, electrification and finishing works from the date of Project Completion Certificate / completion and handover.`,
    15,
    y,
    178,
    5
  );

  y += 4;

  doc.setFont("times", "bold");
  doc.text("During the guarantee period:", 15, y);
  y += 5;

  doc.setFont("times", "normal");
  doc.circle(20, y - 1, 0.7, "F");
  y = addWrappedText(
    doc,
    "Any cracks, settlement, leakage, structural defect, paint peel-off, or electrical fault must be rectified within 7 days of notice.",
    25,
    y,
    160,
    5
  );

  addFooter(doc, 4);
}
function addPage5(doc, data) {
  addPageBorder(doc);

  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  doc.circle(20, y - 1, 0.7, "F");
  y = addWrappedText(
    doc,
    "Failure to rectify defects shall result in deduction from Performance Security, Security Deposit, Retention Amount, Running Account Bills, Final Bills or any other amount payable to the Contractor.",
    25,
    y,
    160
  );

  y += 5;

  doc.circle(20, y - 1, 0.7, "F");
  y = addWrappedText(
    doc,
    "All rectification works shall be carried out exclusively at Contractor's own cost and risk.",
    25,
    y,
    160
  );

  y += 5;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "The decision of SAAM / PMC regarding defects, deficiencies, quality issues and rectification requirements shall be binding upon the Contractor.",
    15,
    y,
    155
  );

  y += 5;

  doc.line(15, y, 195, y);

  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);

  doc.text(
    "4. Performance Security Deposit",
    15,
    y
  );

  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

   y = addWrappedText(
    doc,
    `The Contractor shall furnish Performance Security / Security Deposit of Rs. ${Number(
      data.performanceSecurity || 250000
    ).toLocaleString("en-IN")} (Rupees Two Lakh Fifty Thousand Only).`,
    15,
    y,
    155
  );

  y += 5;

  doc.setFont("times", "bold");
  doc.text("Conditions:", 15, y);

  y += 5;

  doc.setFont("times", "normal");

  const conditions = [
    "Performance Security shall be furnished within 7 days from the date of Letter of Award.",
    "Performance Security shall remain valid for the entire Work Period plus Defect Liability Period.",
    `Performance Security / Security Deposit shall be released only after expiry of ${data.defectLiabilityMonths} months from the date of Project Completion Certificate.`,
  ];

  conditions.forEach((item, index) => {
    doc.text(`${index + 1}.`, 22, y);

    y = addWrappedText(
      doc,
      item,
      30,
      y,
      150
    );

    y += 4;
  });

  y += 3;

  doc.circle(35, y - 1, 0.6, "F");
  y = addWrappedText(
    doc,
    "All defects rectified.",
    40,
    y,
    140
  );

  y += 3;

  doc.circle(35, y - 1, 0.6, "F");
  y = addWrappedText(
    doc,
    "All bills settled.",
    40,
    y,
    140
  );

  y += 3;

  doc.circle(35, y - 1, 0.6, "F");
  y = addWrappedText(
    doc,
    "No pending claim, recovery or dispute.",
    40,
    y,
    140
  );

  y += 6;

  doc.setFont("times", "bold");

  doc.text(
    "If Contractor fails to furnish Performance Security:",
    15,
    y
  );

  y += 5;

  doc.setFont("times", "normal");

  const recoveryPoints = [
    "Recovery shall first be made from Earnest Money Deposit (EMD).",
    "If EMD is insufficient, balance shall be recovered from Running Account Bills (RA Bills).",
    "If still insufficient, recovery shall be made from Final Bill, Retention Amount or any other amount payable.",
    "Recovered amount shall be treated as Security Deposit.",
    `Refund shall be made only after ${data.defectLiabilityMonths} months from the date of Project Completion Certificate.`,
  ];

  recoveryPoints.forEach((item) => {
    doc.circle(20, y - 1, 0.7, "F");

    y = addWrappedText(
      doc,
      item,
      25,
      y,
      160
    );

    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "Failure to comply within 15 days may result in cancellation of LOA, forfeiture of EMD and award of work to another agency at the risk and cost of the Contractor.",
    15,
    y,
    155
  );

  addFooter(doc, 5);
}
function formatINR(amount) {
  return `Rs. ${Number(amount || 0).toLocaleString("en-IN")}`;
}

function addPage6(doc, data) {
  addPageBorder(doc);

  let y = 22;

  const l1Amount = Number(data.l1Amount);
  const gstRate = Number(data.gstRate);
  const gstAmount = Number(
    data.gstAmount || Math.round((l1Amount * gstRate) / 100)
  );
  const totalAmount = Number(data.totalAmount || l1Amount + gstAmount);

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("5. Scope of Work and Rates Applicable", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The total estimated value of works to be executed for the Civil, PEB and Electrical components has been estimated based on the rates submitted by the Contractor and finalized through the tender evaluation process.",
    15,
    y,
    165
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    `The work is hereby awarded at ${formatINR(
      l1Amount
    )} excluding GST.`,
    15,
    y,
    165
  );

  y += 5;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    `GST at the applicable rate of ${gstRate}% shall be payable as per applicable GST law, subject to submission of valid tax invoice and statutory compliance.`,
    15,
    y,
    165
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    `Total Work Value including GST = ${formatINR(
      totalAmount
    )}.`,
    15,
    y,
    165
  );

  y += 4;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    "The Contractor shall be paid strictly as per the attached Bill of Quantities and unit rates in Annexure-I for Civil, PEB and Electrical works, subject to:",
    15,
    y,
    165
  );

  y += 5;

  const paymentConditions = [
    "Documentary evidence;",
    "Actual work done on site;",
    "Joint measurement and verification;",
    "GPS-tagged stage-wise photographs uploaded on the SAAM Tender Management Portal;",
    "Quality test reports and material invoices;",
    "Certification by Engineer-In-Charge / PMC of SAAM.",
  ];

  paymentConditions.forEach((item) => {
    doc.circle(22, y - 1, 0.7, "F");
    y = addWrappedText(doc, item, 27, y, 145);
    y += 3;
  });

  y += 3;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    `Each Running Account (RA) Bill shall be of minimum amount ${formatINR(
      data.raBillMinimum || 525000
    )} excluding GST.`,
    15,
    y,
    165
  );

  y += 5;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    "The Final Bill may be of any amount and shall not be subject to minimum value condition.",
    15,
    y,
    165
  );

  y += 5;

  y = addWrappedText(
    doc,
    "Any Advance Payment towards acceptance of offer and mobilization, if approved by SAAM, shall not exceed 10% of the total work value and shall be released only against submission of equivalent Advance Bank Guarantee issued by a nationalized bank or reputed scheduled bank acceptable to SAAM.",
    15,
    y,
    165
  );

  y += 5;

  y = addWrappedText(
    doc,
    "The mobilization advance, if any, shall be recovered proportionately from Running Account Bills and/or Final Bill until full recovery is completed.",
    15,
    y,
    165
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "No rate escalation, price variation or revision in unit rates shall be allowed during the entire execution period unless specifically approved in writing by SAAM.",
    15,
    y,
    165
  );

  y += 5;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    "The unit rates mentioned in Annexure-I shall remain valid throughout the execution of works. Construction drawings for the project are provided at Annexure-II.",
    15,
    y,
    165
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "All auto-generated values such as L1 Amount, GST Amount, Total Work Value and Project Capacity shall be fetched from the SAAM Tender Management Portal and shall form part of this Letter of Award.",
    15,
    y,
    165
  );

  addFooter(doc, 6);

}
function addSectionLine(doc, y) {
  doc.setLineWidth(0.3);
  doc.line(15, y, 195, y);
}

function addBullet(doc, text, x, y, width) {
  doc.circle(x, y - 1, 0.7, "F");
  return addWrappedText(doc, text, x + 5, y, width);
}

function addPage7(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("6. Advance Bank Guarantee", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "An Advance Bank Guarantee (ABG), wherever mobilization advance is approved by SAAM, shall be mandatory and shall be submitted before release of any advance payment.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  doc.text("The ABG shall be:", 15, y);
  y += 5;

  doc.setFont("times", "normal");
  y = addBullet(doc, "Valid for 6 months from the effective date; OR", 25, y, 155);
  y += 4;

  y = addBullet(
    doc,
    "Valid until the entire advance payment is fully recovered through Running Account Bills / Final Bill, whichever is later.",
    25,
    y,
    155
  );
  y += 6;

  y = addWrappedText(
    doc,
    "SAAM may require extension of ABG validity from time to time until full recovery of advance payment. Failure to extend ABG validity shall entitle SAAM to withhold payments, recover the advance from RA Bills / Final Bill / Security Deposit, or invoke the ABG.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("7. Adjustment of Advance by SAAM", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "SAAM shall adjust the mobilization advance, if any, while settling Running Account Bills. Recovery shall continue until the entire advance amount is fully adjusted.",
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "10% of each RA Bill shall be retained by SAAM as retention amount, unless otherwise decided by SAAM in writing.",
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "During settlement of the Final Bill, SAAM shall retain / adjust the balance of:",
    15,
    y,
    155
  );

  y += 4;

  const retainItems = [
    "Mobilization advance pending;",
    "Any recoveries;",
    "Penalties / Liquidated Damages, if applicable;",
    "Defect rectification cost;",
    "Any statutory dues, taxes, labour liabilities or third-party claims arising due to Contractor.",
  ];

  retainItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 4;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Only the net payable amount, after all recoveries and deductions, shall be released to the Contractor.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("8. Payment Terms", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    `Payment shall be released within ${data.paymentReleaseDays || 10} days after certification of RA Bills by the Engineer-In-Charge of SAAM / PMC, subject to availability of funds, correctness of bill, portal verification and completion of required documentation.`,
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  doc.text("Mandatory for payment release:", 15, y);
  y += 5;

  doc.setFont("times", "normal");

  const paymentDocs = [
    "GPS-tagged work stage photographs uploaded on SAAM Tender Management Portal;",
    "Quality test reports;",
    "Material invoices;",
    "Joint measurement sheet;",
    "On-site physical verification;",
    "Valid GST invoice, PAN, GSTIN and statutory compliance documents.",
  ];

  paymentDocs.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 7);
}

function addPage8(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("9. Liquidated Damages (LD Clause)", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "In case of delay attributable to the Contractor beyond the approved project completion period or milestone schedule, Liquidated Damages shall be imposed by SAAM.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "LD shall be calculated at 1% of the total contract value for every full week of delay, subject to a maximum of 5% of the Total Contract Amount.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    "LD shall be without prejudice to SAAM’s right to recover actual losses, withhold payments, adjust amounts from Security Deposit / Retention / RA Bills / Final Bill, terminate the LOA or get the remaining work executed through another agency at the risk and cost of the Contractor.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "Any extension of time shall be valid only if approved in writing by SAAM. Mere continuation of work shall not be treated as waiver of delay, penalty, LD or other contractual rights of SAAM.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("10. Contractor’s Arrangements", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall make their own arrangements at their own cost for:",
    15,
    y,
    155
  );

  y += 4;

  const arrangements = [
    "Supply of water;",
    "Light / power;",
    "Labour hutments;",
    "Labour safety;",
    "Food and accommodation for all workers;",
    "Tools, tackles, machinery, scaffolding and temporary works;",
    "Site security, housekeeping and barricading.",
  ];

  arrangements.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "Contractor shall comply with all applicable safety, labour, environmental, statutory and local authority norms. No extra payment shall be made for such arrangements unless specifically approved in writing by SAAM.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("11. Mandatory Safety Requirements", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "All required and mandatory Safety Personal Protective Equipment (PPE) for workers, staff, visitors and supervisory personnel shall be provided exclusively by the Contractor at its own cost.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  doc.text("This includes:", 15, y);
  y += 5;

  doc.setFont("times", "normal");

  const ppeItems = [
    "Helmets;",
    "Safety shoes;",
    "Safety belts;",
    "Gloves;",
    "High visibility jackets;",
    "Eye protection;",
    "Any other safety equipment required by law or instructed by SAAM / PMC.",
  ];

  ppeItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 8);
}
function addPage9(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const safetyContinue = [
    "High visibility jackets;",
    "Eye protection;",
    "Any other safety equipment as required by law or instructed by SAAM / PMC.",
  ];

  safetyContinue.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("II. General Terms and Conditions", 15, y);
  y += 11;

  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "1. “Company” means Shine Agri Allied & Marketing Multistate Cooperative Society Limited (SAAM), including its authorised officers, representatives, engineers, consultants and assigns.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "2. “Contractor” means the entity to whom SAAM has issued this Letter of Award and shall include its proprietor, partners, directors, employees, engineers, representatives, successors and permitted assigns.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "3. “Letter of Award” means this contractual document issued by SAAM to the Contractor, binding the Contractor to fulfil all requirements, obligations, specifications, timelines, payment conditions, quality standards and terms stated herein.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("4. Submission of BoQ", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor must submit its rates within the Bill of Quantities (BoQ) provided by SAAM on the Contractor’s letterhead.",
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "All pages of the filled-in BoQ must bear the Contractor’s signature with rubber stamp. Rates must be provided in both figures and words.",
    15,
    y,
    155
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "The BoQ must be clean, legible, complete and free from erasures, overwriting or ambiguity. In case of discrepancy between figures and words, the rate written in words shall prevail, unless SAAM decides otherwise in writing.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("5. Supply & Rate Inclusion", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "All items in the BoQ shall be supplied, arranged, transported, stored, handled and executed by the Contractor at its own cost, including but not limited to:",
    15,
    y,
    155
  );

  y += 4;

  const supplyItems = [
    "Cement;",
    "Steel;",
    "RMC;",
    "Aggregates;",
    "All necessary Civil / PEB / Electrical materials;",
    "Labour, machinery, tools, tackles, shuttering, scaffolding and safety arrangements;",
    "Loading, unloading, transportation, storage and shifting at site.",
  ];

  supplyItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "No separate payment shall be made for any incidental, ancillary or enabling work unless specifically provided in the BoQ and approved by SAAM in writing.",
    15,
    y,
    155
  );

  addFooter(doc, 9);
}
function addPage10(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "shall be supplied by the Contractor at its own cost, risk and responsibility.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "The Contractor shall also provide all services, labour, machinery, equipment, tools, tackles and incidental arrangements required for proper execution, including but not limited to:",
    15,
    y,
    155
  );

  y += 4;

  const services = [
    "Placing;",
    "Binding;",
    "Bar fitting;",
    "Pumping;",
    "Casting;",
    "Vibrating;",
    "Compaction;",
    "Curing of concrete;",
    "Cleaning, finishing and protection of completed work.",
  ];

  services.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "6. It is deemed that the Contractor has visited the site, examined site conditions, verified drawings, checked access, water, power, soil, surroundings, labour availability and all local conditions before quoting rates in the BoQ.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "No claim shall be entertained later on the ground of ignorance of site conditions, access constraints, local issues, material handling difficulties or any other condition that could have been reasonably verified before submission of bid.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("7. Rate Inclusions", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The rates include, whether specifically stated or not, all operations, materials, labour and incidental works required for proper and complete execution of the item, including:",
    15,
    y,
    155
  );

  y += 4;

  const rateItems = [
    "Cutting;",
    "Chasing;",
    "Holes in walls;",
    "Making good and reinstating items to original condition wherever required;",
    "Finishing, cleaning, curing and protection of completed work;",
    "All leads, lifts, loading, unloading and shifting within site.",
  ];

  rateItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "8. Rates shall include shoring, bracing, planking, barricading, temporary supports, side slope protection, dewatering arrangements and all safety measures required to prevent soil fall, collapse, accident or damage during excavation and foundation works.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "The Contractor shall be fully responsible for any damage, accident, injury, settlement or failure arising due to inadequate support, protection, dewatering or unsafe execution.",
    15,
    y,
    155
  );

  addFooter(doc, 10);
}
function addPage11(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "9. Rates include slush removal, side dressing, cleaning of foundation pits, dewatering arrangements, disposal of unsuitable material and all miscellaneous preparatory works required until concrete placement.",
    15,
    y,
    155
  );

  y += 4;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    "No extra claim shall be admissible for such incidental or preparatory works unless expressly provided in the BoQ and approved by SAAM in writing.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("10. Q.R.O. – Quote Rate Only", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Where required by SAAM’s Engineer-In-Charge / Site Engineer, the Contractor shall submit detailed rate analysis, supporting documents, material break-up, labour component, machinery component, GST component and justification for quoted rates.",
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "SAAM reserves the right to examine, approve, reject, negotiate or restrict any rate based on technical, financial or market reasonableness.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("11. Basic Rate Coverage", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The basic rate of all bought-out items shall include:",
    15,
    y,
    155
  );

  y += 4;

  const coverage = [
    "Landed price at site;",
    "Cost of materials, labour, templates, scaffolding, nails, tools and tackles;",
    "Loading / unloading at all levels;",
    "All operations necessary for proper execution;",
    "Storage, stacking, shifting, protection and handling at site;",
    "Wastage, breakage, testing, samples and compliance requirements wherever applicable.",
  ];

  coverage.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "All such costs shall be deemed included in the Contractor’s quoted rates and shall be borne entirely by the Contractor.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  y = addWrappedText(
    doc,
    "12. For items of different makes, the Contractor shall provide equivalent makes matching the approved technical specifications, subject to prior written approval of SAAM Engineer-In-Charge.",
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "The decision of SAAM Engineer-In-Charge regarding approval or rejection of make, brand, quality, specification or equivalency shall be final and binding upon the Contractor.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("13. GST Rates", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    `The Contractor must fill GST rate column in the BoQ as per applicable GST percentage. For this Letter of Award, GST shall be calculated as per applicable rate, presently ${data.gstRate || 18}% unless changed by law.`,
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "GST payment shall be subject to valid tax invoice, GSTIN, proper filing of returns, statutory compliance and availability of input tax credit wherever applicable. Any GST mismatch, non-filing, penalty or statutory liability caused due to Contractor shall be recoverable from the Contractor.",
    15,
    y,
    155
  );

  addFooter(doc, 11);
}
function addPage12(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("14. FOR Delivery", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Unless otherwise specified, prices shall be on FOR (Free on Road) destination basis up to the following receiving location:",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    `${data.pacsName}, at ${data.receivingLocation}.`,
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    "All risks, liabilities, damages, losses, theft, breakage, shortage or deterioration shall remain with the Contractor until safe delivery, inspection and acceptance at site by SAAM / PMC.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("15. No Revision in Rates", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "No rate revision, price escalation, market variation, fuel escalation, labour escalation, material escalation or any other increase shall be allowed during the entire duration of the project.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "The quoted and accepted rates shall remain firm and binding until completion and handover of the project, unless specifically approved in writing by SAAM.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("16. Performance Security", 15, y);
  y += 6;

  const securityAmount = data.performanceSecurity || 250000;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    `The Contractor must furnish Performance Security of ${formatINR(
      securityAmount
    )} within 10 days from the date of Letter of Award.`,
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "The Performance Security may be furnished by way of Bank Guarantee or may be recovered / adjusted by SAAM from EMD, Running Account Bills, Final Bill, Retention Amount or any other amount payable to the Contractor.",
    15,
    y,
    155
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    `Performance Security / Security Deposit shall be released only after completion of ${data.defectLiabilityMonths} months from the date of Project Completion Certificate, subject to satisfactory completion of Defect Liability Period, rectification of all defects and settlement of all claims.`,
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "normal");
  y = addWrappedText(
    doc,
    "Failure to furnish Performance Security shall give SAAM full authority to:",
    15,
    y,
    155
  );

  y += 4;

  const securityActions = [
    "Recover the amount from EMD;",
    "Recover the balance, if any, from RA Bills / Final Bill / Retention Amount;",
    "Cancel the LOA;",
    "Invite fresh quotations / award work to another agency at the risk and cost of the Contractor.",
  ];

  securityActions.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 12);
}

function addPage12Continuation(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("17. Construction Phase (Execution on Site)", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Work shall begin only upon submission and approval of the following documents through SAAM Tender Management Portal / physical submission as required:",
    15,
    y,
    155
  );

  y += 4;

  const docs = [
    "Contractor’s QA/QC Plan;",
    "Prior information related to plant and machinery;",
    "Day-wise Method of Work Plan;",
    "Contractor’s Environment & Safety Management Plan;",
    "Emergency Evacuation Plan;",
    "Labour deployment plan and safety compliance plan.",
  ];

  docs.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "No work shall commence until the above documents are submitted and approved by SAAM / PMC.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFontSize(11);
  doc.text("18. Daily Reporting", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Contractor must furnish daily status reports related to progress, manufacturing, material procurement, delivery, labour deployment, machinery deployment and site execution through the SAAM Tender Management Portal or in such manner as instructed by SAAM / PMC.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "SAAM / PMC may visit the Contractor’s godown, factory, fabrication yard, storage location or project site at any time for inspection, verification of materials, quality checks, progress review or compliance audit.",
    15,
    y,
    155
  );

  addFooter(doc, 12);
}
function addPage13(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("19. Quality Assurance & Control", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall establish and maintain Quality Assurance and Quality Control arrangements at the construction site. Wherever required by SAAM / PMC, the Contractor shall also arrange necessary testing facility, laboratory support, testing agency or approved external laboratory at its own cost.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  doc.text("The Contractor must:", 15, y);
  y += 5;

  doc.setFont("times", "normal");

  const qcItems = [
    "Maintain a complete Quality Control Register;",
    "Conduct all tests as per Annexure-III, applicable IS Codes, CPWD specifications and instructions of SAAM Engineer-In-Charge;",
    "Provide adequate manpower, equipment, samples and testing arrangements;",
    "Replace defective, rejected or non-conforming materials immediately;",
    "Rectify rejected works at its own cost and risk;",
    "Upload quality reports, test certificates, material invoices and stage-wise photographs on the SAAM Tender Management Portal wherever applicable.",
  ];

  qcItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "SAAM Engineer-In-Charge / PMC may reject any work, material, method, workmanship or supply not conforming to approved specifications, drawings, quality standards or instructions.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("20. Measurement & Documentation", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Measurement entries shall be recorded stage-wise and shall be jointly verified / signed, physically or digitally, by the following:",
    15,
    y,
    155
  );

  y += 4;

  const measurementItems = [
    "Contractor’s authorised representative;",
    "SAAM Engineer-In-Charge;",
    "PMC representative, if applicable;",
    "Any other authorised technical officer nominated by SAAM.",
  ];

  measurementItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "The Contractor shall provide all assistance, manpower, tools, records, drawings, bills, invoices, photographs and supporting documents required for measurement, documentation and verification.",
    15,
    y,
    155
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "No payment shall be processed unless the relevant measurements, documents and verification records are completed to the satisfaction of SAAM / PMC.",
    15,
    y,
    155
  );

  addFooter(doc, 13);
}
function addPage14(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("20.1 Permits, Approvals & Local Permissions", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall be solely responsible for obtaining all permits, permissions, approvals, licenses, registrations and statutory clearances required for execution of the Project, including but not limited to:",
    15,
    y,
    155
  );

  y += 5;

  const permits = [
    "Establishing batching plant;",
    "Electricity connection;",
    "Environmental clearance;",
    "Pollution control clearance;",
    "Delivery and movement of goods;",
    "Movement / operation of Contractor’s equipment;",
    "Licenses required by Municipality / Police / Local Authorities;",
    "Any labour, safety, transport, machinery or statutory permissions required under applicable law.",
  ];

  permits.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "All expenditure, fees, penalties, charges and legal compliance responsibilities shall lie entirely with the Contractor. SAAM shall not be liable for any delay, penalty, prosecution, loss or claim arising due to non-compliance by the Contractor.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("21. Notices to Public Bodies & Statutory Compliance", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(doc, "The Contractor shall:", 15, y, 155);
  y += 4;

  const statutory = [
    "Give all statutory notices required by law;",
    "Obtain all required licenses, registrations, permissions and approvals;",
    "Pay all fees, taxes, penalties, charges and levies arising out of its operations;",
    "Maintain proper barricading, temporary enclosures and site protection;",
    "Repair damages caused to adjoining public or private properties at its own cost;",
    "Maintain lighting, watch and ward, traffic control and safety arrangements during night work;",
    "Ensure compliance with labour laws, safety laws, environmental laws, GST laws and local authority requirements;",
    "No extra payment shall be made for any such compliance activities.",
  ];

  statutory.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 6;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Any liability arising due to violation of statutory compliance by the Contractor shall be recoverable from the Contractor’s bills, Security Deposit, Retention Amount or any other payable amount.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("22. Safety Regulations & Housekeeping", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(doc, "The Contractor shall strictly comply with:", 15, y, 155);
  y += 4;

  const safety = [
    "All applicable safety regulations;",
    "Provide protection to all persons on site;",
    "Maintain safe access, safe working platforms and safe machinery operation;",
  ];

  safety.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 14);
}
function addPage15(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const safetyContinue = [
    "Ensure the site is free from obstructions;",
    "Avoid hazards to workers, visitors, PACS members and adjoining public;",
    "Maintain safety signage, barricading, fire extinguishers and emergency access;",
    "Provide round-the-clock guarding, watch and ward and site security until completion and handover.",
  ];

  safetyContinue.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("23. First Aid & Medical Compliance", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall maintain first aid and medical support at a readily accessible location at the project site, including:",
    15,
    y,
    155
  );

  y += 4;

  const firstAid = [
    "First aid appliances;",
    "Sterilized dressings;",
    "Sterilized cotton wool;",
    "Basic medical kit;",
    "Emergency contact details and nearest hospital information;",
    "Responsible person assigned for first aid during working hours.",
  ];

  firstAid.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "A responsible person shall be assigned by the Contractor to manage first aid, maintain emergency records and coordinate immediate medical response in case of accident or injury.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("24. Daily Reports & Weekly Review Meetings", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(doc, "Contractor must submit:", 15, y, 155);
  y += 4;

  const reports = [
    "Daily Construction Work Done Report to SAAM Engineer-In-Charge / PMC;",
    "Daily labour, machinery and material deployment status;",
    "Weekly review meeting updates and compliance reports;",
    "Outstanding issues, delays, bottlenecks and progress details;",
    "GPS-tagged photographs and portal updates wherever applicable.",
  ];

  reports.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(doc, "The following must attend weekly review meetings:", 15, y, 155);
  y += 4;

  const attendees = [
    "Project Manager of Contractor;",
    "Supervisory team;",
    "SAAM Engineer-In-Charge;",
    "PMC representative, if required;",
    "Any technical consultant or officer nominated by SAAM.",
  ];

  attendees.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("25. Coordination & Technical Documentation", 15, y);

  addFooter(doc, 15);
}
function addPage16(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "SAAM may deduct any statutory liabilities, penalties, taxes, labour dues, safety liabilities, damages or third-party claims from the Contractor’s bills, Security Deposit, Retention Amount or any other payable amount if the Contractor fails to discharge such liabilities.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("29. Transportation, Delivery & Rejection of Goods", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  y = addWrappedText(doc, "Contractor must ensure:", 15, y, 155);
  y += 4;

  const transportItems = [
    "Safe transportation and delivery as per SAAM’s instructions;",
    "Documentation for all goods delivered;",
    "Proper marking on every package;",
    "SAAM shall accept goods only after inspection, testing and verification;",
    "Contractor must take back rejected goods at its own cost;",
    "Rejected goods must be lifted within 7 days of intimation;",
    "Any loss, damage, shortage or deterioration during transit shall be Contractor’s responsibility.",
  ];

  transportItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(doc, "Failure shall lead to:", 15, y, 155);
  y += 4;

  const failureItems = [
    "Non-payment;",
    "Recovery from bills;",
    "Penalty as per contract;",
    "Rejection of material;",
    "Recovery of additional cost incurred by SAAM.",
  ];

  doc.setFont("times", "normal");
  failureItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "Original bills, delivery challans, test certificates, e-way bills, invoices and all supporting documentation must be submitted to SAAM’s Accounts Department / uploaded on the SAAM Tender Management Portal as required.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("30. Insurance", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor must ensure adequate insurance coverage for goods, materials, tools, machinery, workers and transit movement, including transit insurance, at its own cost.",
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "SAAM shall not be responsible for any loss, theft, accident, damage, fire, transit loss, labour injury or third-party claim arising before completion and handover.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("31. Payment for Supplies", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  y = addWrappedText(doc, "Payments shall be released by SAAM:", 15, y, 155);
  y += 4;

  const paymentItems = [
    "On milestone basis;",
    "Against valid invoices with GST details;",
    "Subject to PAN, GSTIN, e-way bill and statutory compliance;",
  ];

  paymentItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 16);
}
function addPage17(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addBullet(
    doc,
    "Subject to approval by SAAM Engineer-In-Charge / PMC;",
    25,
    y,
    155
  );
  y += 3;

  y = addBullet(
    doc,
    "Only after completion and verification of milestone, not before;",
    25,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  // 32
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("32. Payment of Bills", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Unless otherwise agreed in writing, the Contractor shall submit bills within 10 days after receipt of materials, completion of work or achievement of milestone, as applicable.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "SAAM shall have full discretion to:",
    15,
    y,
    155
  );

  y += 4;

  const billRights = [
    "Modify quantities;",
    "Restrict quantities or ceiling limits;",
    "Approve or reject any item;",
    "Adjust payments based on actual work done;",
    "Recover penalties, advances, statutory liabilities and damages;",
    "Withhold payments for quality, safety or contractual non-compliance.",
  ];

  doc.setFont("times", "normal");

  billRights.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  // 33 Warranty
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("33. Warranty", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall provide warranty for all goods, materials, components and systems supplied under the Contract for their performance, durability, intended usage and compliance with specifications.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "Warranty Period: 2 (Two) Years from the date of acceptance and completion of works by SAAM.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "normal");

  y = addWrappedText(
    doc,
    "If during the warranty period any item is found defective, under-performing, damaged, non-functional or non-compliant, the Contractor shall repair, replace or rectify the same at its own cost to the satisfaction of SAAM.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "SAAM shall have full authority to determine non-performance, defect, failure or deficiency and the Contractor shall abide by such determination.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "PEB Structural Warranty: 20 Years.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "normal");

  y = addWrappedText(
    doc,
    "Failure to honour warranty obligations shall entitle SAAM to recover rectification cost from Security Deposit, Retention Amount, Contractor Bills or any other amount payable.",
    15,
    y,
    155
  );

  addFooter(doc, 17);
}

function addPage17Continuation(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("34. Time is the Essence of Contract", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall strictly comply with all timelines, milestone schedules, progress targets and completion obligations under this Letter of Award.",
    15,
    y,
    155
  );

  y += 4;

  const defaultEvents = [
    "Delay;",
    "Non-compliance;",
    "Failure to meet approved schedule;",
    "Failure to mobilize resources;",
    "Failure to achieve milestone progress;",
    "Failure to meet delivery schedule;",
  ];

  defaultEvents.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 6;
  y = addWrappedText(doc, "SAAM reserves the right to:", 15, y, 155);
  y += 5;

  const saamRights = [
    "Impose reasonable liquidated damages in accordance with this Letter of Award;",
    "Recover the loss from any bill, Security Deposit, Performance Security, Retention Amount or any other amount payable;",
    "Cancel the Letter of Award in accordance with its termination provisions;",
    "Source goods / services / remaining works from any third party at Contractor’s risk and cost;",
    "Recover any reasonable incremental cost incurred by SAAM due to replacement sourcing.",
  ];

  saamRights.forEach((item, index) => {
    doc.setFont("times", "normal");
    doc.text(`${index + 1}.`, 25, y);
    y = addWrappedText(doc, item, 34, y, 150);
    y += 4;
  });

  y += 5;
  doc.setFont("times", "bold");
  addWrappedText(
    doc,
    "The Contractor shall be liable for any reasonable incremental cost, delay cost, third-party claim or loss incurred by SAAM due to such replacement sourcing, subject to applicable law and without double recovery.",
    15,
    y,
    155
  );

  addFooter(doc, 17);
}
function addPage18(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("35. Insurance for Machinery & Plant", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(doc, "The Contractor using any of the following:", 15, y, 155);
  y += 4;

  const machinery = [
    "Machinery;",
    "Trucks;",
    "Vibrators;",
    "Rollers;",
    "Excavators;",
    "Cranes, welding machines, lifting tools and any other equipment used for the Project.",
  ];

  machinery.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "must obtain and maintain valid insurance coverage against:",
    15,
    y,
    155
  );

  y += 4;

  const insuranceRisks = [
    "Damage;",
    "Accident;",
    "Theft;",
    "Loss;",
    "Fire;",
    "Third-party liability;",
    "Injury to workers or persons at site.",
  ];

  insuranceRisks.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "Such insurance shall remain valid for all machinery, plant, vehicles, tools, equipment and workers employed for SAAM’s Project. SAAM shall not be responsible for any uninsured loss.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("36. Workmen Compensation Policy", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall procure, maintain and implement a valid Workmen Compensation Insurance Policy for all workers, labourers, supervisors, technicians and persons deployed during execution of the Project.",
    15,
    y,
    155
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Any injury, accident, death, compensation claim or labour liability shall be the sole responsibility of the Contractor and shall be recoverable from the Contractor if borne by SAAM.",
    15,
    y,
    155
  );

  addFooter(doc, 18);
}

function addPage19(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("37. Ownership & Intellectual Property Rights (IPR)", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const iprItems = [
    "Ownership of all goods, materials, documents, drawings, records and deliverables shall transfer to SAAM / Project Owner at the place of delivery, free from encumbrances;",
    "Contractor shall not claim any IPR, lien, ownership or retention right on materials or deliverables supplied under this Letter of Award;",
    "IPR in goods, materials, technical documents, drawings, project records and deliverables shall belong exclusively to SAAM / Project Owner, to the extent applicable;",
    "Contractor shall defend and indemnify SAAM against any third-party IPR, ownership, quality or supply-chain claims;",
    "Any legal cost, claim, penalty or loss borne by SAAM shall be recoverable from the Contractor;",
    "For third-party procured goods, Contractor must ensure manufacturer warranty and transfer of rights / warranty benefits to SAAM / Project Owner.",
  ];

  iprItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 4;
  });

  y += 5;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("38. Sub-contracting Restrictions", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const subItems = [
    "The Contractor shall not sub-contract any portion of this Letter of Award to any third party without prior written approval of SAAM;",
    "The Contractor shall remain fully responsible for acts, omissions, defaults, negligence and non-compliance of its sub-contractors, if any;",
    "Contractor shall indemnify SAAM for any loss, damage, delay, defect, injury, claim or liability caused by sub-contractors;",
    "Approval of sub-contractor, if granted by SAAM, shall not relieve the Contractor from any contractual responsibility.",
  ];

  subItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 4;
  });

  y += 5;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("39. Failure to Execute Work", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  y = addWrappedText(doc, "If the Contractor fails to comply with terms, SAAM may:", 15, y, 155);
  y += 4;

  const failureWork = [
    "Cancel the order / LOA immediately;",
    "Procure remaining work from another agency;",
    "Recover all additional costs from Contractor;",
    "Adjust recoverable amounts from any bill, Security Deposit, Retention Amount, EMD or other payable amount;",
    "Blacklist / debar the Contractor from future SAAM projects, subject to SAAM’s policy.",
  ];

  failureWork.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 19);
}
function addPage20(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Before cancellation, SAAM may, at its discretion, give the Contractor an opportunity to remedy the breach within such time as may be specified by SAAM.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Under no circumstances shall SAAM be liable for compensating the Contractor for cancellation, termination, risk purchase or replacement execution.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("40. Recovery of Amounts", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Any money payable by the Contractor to SAAM shall be recoverable from:",
    15,
    y,
    155
  );

  y += 4;

  const recovery = [
    "Contractor’s bills;",
    "Security Deposit / Performance Security;",
    "Retention money;",
    "EMD;",
    "Final Bill;",
    "Any other amount payable to the Contractor under this or any other SAAM project.",
  ];

  recovery.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "SAAM shall have full discretionary rights for such recovery, adjustment, withholding or set-off.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("41. Compliance with Applicable Laws", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall comply with all applicable laws, rules, regulations, notifications, circulars, guidelines and statutory requirements related to:",
    15,
    y,
    155
  );

  y += 4;

  const laws = [
    "Labour;",
    "Safety;",
    "Environment;",
    "GST;",
    "Local bodies;",
    "State and Central Government Acts;",
    "Notifications, circulars and guidelines;",
    "Municipal, police, transport and pollution control requirements.",
  ];

  laws.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Non-compliance shall be treated as breach of contract and all consequences, penalties, losses and liabilities shall be borne by the Contractor.",
    15,
    y,
    155
  );

  addFooter(doc, 20);
}

function addPage20Continuation(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("42. Occupational Safety & Environmental Compliance", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  y = addWrappedText(doc, "Contractor must comply with:", 15, y, 155);
  y += 4;

  const ehs = [
    "EHS (Environmental Health & Safety) Regulations;",
    "Environmental norms;",
    "Risk management guidelines;",
    "Labour safety rules;",
    "Fire safety requirements;",
    "Pollution control and waste management requirements.",
  ];

  ehs.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 20);
}
function addPage21(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("43. SAAM Not Liable for External Failures", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "SAAM shall not be liable for failure, delay, loss, cost overrun or non-performance of the Contractor due to:",
    15,
    y,
    155
  );

  y += 4;

  const failures = [
    "Labour strikes;",
    "Lockouts;",
    "Shortage of labour;",
    "Machinery breakdown;",
    "Railway delay;",
    "Shortage of wagons;",
    "Raw material shortages;",
    "Transport disruption;",
    "Vendor / supplier failure;",
    "Force majeure events;",
  ];

  failures.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "The Contractor shall proactively plan, manage and mitigate such risks and shall not claim extension, compensation or rate revision unless specifically approved in writing by SAAM.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("44. Change in Constitution", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  y = addWrappedText(doc, "Contractor must inform SAAM immediately in writing about:", 15, y, 155);
  y += 4;

  const changes = [
    "Change in ownership;",
    "Partnership change;",
    "Firm type change;",
    "Change in directors, partners, proprietor or authorized signatory;",
    "Change in registered office, GSTIN, PAN, bank account or legal status;",
    "Any insolvency, bankruptcy, legal restriction or blacklisting event.",
  ];

  changes.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Failure to disclose such change may lead to withholding of payment, termination of LOA, forfeiture / recovery of Security Deposit and other action as deemed fit by SAAM.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("45. Transport Vehicle Documents", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Any transport vehicle carrying materials, equipment or goods for the Project must produce valid documents before entry / unloading at site.",
    15,
    y,
    155
  );

  addFooter(doc, 21);
}
function addPage22(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const vehicleDocs = [
    "Valid registration certificate;",
    "Insurance;",
    "Pollution certificate;",
    "E-way bill / transport documents;",
    "Proper challans and delivery documents;",
  ];

  vehicleDocs.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Non-compliance shall result in denial of entry, rejection of delivery and all related delay, loss and cost shall be borne by the Contractor.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("46. Representations and Warranties", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor represents, warrants and undertakes that:",
    15,
    y,
    155
  );

  y += 4;

  const reps = [
    "It is legally authorized and competent to execute and perform the Contract with SAAM;",
    "Execution of this Contract does not violate any constitutional documents, agreements, statutory restrictions or obligations;",
    "Execution does not infringe any intellectual property rights, ownership rights or third-party rights;",
    "Execution shall not result in litigation, injunction, sanction or legal restriction affecting the Project;",
    "The Contractor and its sub-contractors shall comply with all applicable laws;",
    "The Contractor has not participated, directly or indirectly, in corruption, bribery, fraud, collusion, kickbacks or unethical practices;",
    "The Contractor shall act honestly, fairly, transparently and in good faith throughout execution of the Project;",
    "The Contractor has not authorized, encouraged or acquiesced to bribery of any type;",
    "Its officers, employees, subsidiaries, agents, consultants and vendors do not participate in bribery practices;",
    "The Contractor shall take all reasonable steps to ensure supply-chain partners comply with anti-bribery and anti-corruption policies;",
    "Neither the Contractor nor its subsidiaries or related entities are subject to sanctions, restricted jurisdictions or prohibited activities;",
    "The Contractor follows anti-corruption, anti-money laundering and compliance laws and maintains internal systems to ensure adherence.",
  ];

  reps.forEach((item, index) => {
    doc.setFont("times", "normal");
    doc.text(`(${index + 1})`, 18, y);

    y = addWrappedText(
      doc,
      item,
      30,
      y,
      155
    );

    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "Any false declaration, concealment of material fact, corruption, bribery, collusion or misrepresentation shall constitute material breach of contract and shall entitle SAAM to terminate the LOA, recover losses and initiate legal proceedings.",
    15,
    y,
    155
  );

  addFooter(doc, 22);
}
function addPage23(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("47. Indemnity", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor shall fully defend, indemnify and hold SAAM, Project Owner, PACS, its officers, employees, engineers, consultants and representatives harmless from and against all:",
    15,
    y,
    155
  );

  y += 4;

  const indemnityItems = [
    "Actions;",
    "Suits;",
    "Claims;",
    "Losses;",
    "Accidents;",
    "Damages;",
    "Injuries;",
    "Penalties;",
    "Interest;",
    "Legal costs;",
    "Third-party claims;",
    "Labour, safety, tax, GST, environmental or statutory liabilities.",
  ];

  indemnityItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 2.5;
  });

  y += 4;

  y = addWrappedText(doc, "arising out of or in connection with:", 15, y, 155);
  y += 4;

  const arising = [
    "Wrong, false or misleading representations made by the Contractor;",
    "Contractor’s misconduct, negligence, fraud, breach or non-compliance during execution;",
    "Any injury, death, accident or property damage caused during execution;",
    "Legal disputes arising from Contractor’s supply, labour, machinery, transport, subcontracting or statutory compliance;",
    "Any claim arising due to defective material, poor workmanship, delayed execution or violation of law.",
  ];

  arising.forEach((item, index) => {
    doc.text(`(${index + 1})`, 18, y);
    y = addWrappedText(doc, item, 30, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "This indemnity shall survive completion, termination, cancellation or expiry of this Letter of Award.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("48. Confidentiality", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor acknowledges that all documents, drawings, BOQs, rates, project data, technical information, portal records, member / PACS details, financial information and information provided by SAAM are confidential and shall not be disclosed to any third party without prior written permission of SAAM.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "This obligation excludes information that is:",
    15,
    y,
    155
  );

  y += 4;

  const confidentialExclusions = [
    "Publicly available at the time of disclosure;",
    "Required to be disclosed by law, court order or statutory authority;",
    "Shared with Contractor’s legal, technical or financial advisors strictly on need-to-know basis and under confidentiality obligation.",
  ];

  confidentialExclusions.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Breach of confidentiality shall entitle SAAM to terminate the LOA, recover damages and initiate legal action.",
    15,
    y,
    155
  );

  addFooter(doc, 23);
}
function addPage24(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "This confidentiality requirement shall remain in effect for one year after termination, completion, cancellation or expiry of the Contract.",
    15,
    y,
    155
  );

  y += 5;

  y = addWrappedText(
    doc,
    "Contractor shall ensure that its employees, consultants, agents, subsidiaries, vendors and subcontractors comply with the same confidentiality obligations.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  // 49
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("49. Termination and Consequences of Termination", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "SAAM may terminate this Contract / Letter of Award under any of the following circumstances:",
    15,
    y,
    155
  );

  y += 4;

  const termination = [
    "Contractor fails to start work within 7 days from the date of LOA;",
    "Non-delivery or delay beyond approved timelines;",
    "Breach of any term, clause or contractual obligation;",
    "Contractor becomes insolvent, bankrupt or legally restricted;",
    "Corruption, fraud, collusion, false declaration or serious misconduct;",
    "Failure to furnish Performance Security or statutory compliance.",
  ];

  termination.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "SAAM may provide opportunity to remedy the breach, however failure to cure such breach shall entitle SAAM to terminate the Contract immediately.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  // 50
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("50. Consequences of Termination", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const consequences = [
    "Contractor shall not claim ownership or IPR rights on goods already taken over by SAAM;",
    "SAAM shall pay only for accepted and verified work after adjustment of recoveries;",
    "Contractor shall not claim refund of Security Deposit for warranty defaults;",
    "SAAM may recover losses from EMD, Security Deposit, Retention Amount, RA Bills, Final Bill or any amount payable;",
    "SAAM may procure remaining work from another agency at Contractor’s risk and cost;",
    "Any work already in process may be completed by Contractor only subject to directions of SAAM.",
  ];

  consequences.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 24);
}
function addPage25(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("51. Survival", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The following provisions shall survive completion, termination, cancellation, suspension or expiry of this Letter of Award:",
    15,
    y,
    155
  );

  y += 4;

  const survival = [
    "Confidentiality;",
    "Indemnity;",
    "Termination consequences;",
    "Damages and recoveries;",
    "Ownership & IPR;",
    "Representations & Warranties;",
    "Warranty obligations;",
    "Anti-bribery & compliance obligations;",
    "Dispute resolution and jurisdiction;",
    "Any recovery, claim or liability pending against the Contractor.",
  ];

  survival.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("52. Conflict of Terms", 15, y);
  y += 6;

  doc.setFont("times", "normal");

  y = addWrappedText(
    doc,
    "In the event of any conflict, inconsistency or ambiguity between Purchase Order terms, Work Order terms, Annexures, Drawings, Schedules or any other document, the provisions of this Letter of Award shall prevail unless otherwise specifically approved by SAAM in writing.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("53. Composition of Contract", 15, y);
  y += 6;

  doc.setFont("times", "normal");

  y = addWrappedText(
    doc,
    "This Letter of Award together with General Terms & Conditions, Annexures, Schedules, Drawings, Technical Specifications, BOQ, Tender Documents, Corrigenda and documents referenced herein shall constitute one integrated and binding Contract between SAAM and the Contractor.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("54. Contractor Code of Conduct", 15, y);
  y += 6;

  doc.text("A. Working Conditions / Labour", 15, y);
  y += 6;

  doc.setFont("times", "normal");

  const labourRules = [
    "Contractor shall not employ child labour at any stage;",
    "Contractor shall comply with minimum wages, working hours, overtime and labour welfare laws;",
    "Forced labour, bonded labour, trafficking and slavery are strictly prohibited;",
    "Contractor shall permit lawful freedom of association and collective bargaining;",
    "Contractor shall provide safe and healthy working conditions at all times.",
  ];

  labourRules.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 25);
}
function addPage26(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.text("Minimum Safety Requirements:", 15, y);
  y += 5;

  doc.setFont("times", "normal");

  const safetyReq = [
    "Proper lighting;",
    "Safe scaffolding;",
    "Safety equipment;",
    "Medical support;",
    "PPE kits;",
    "Safety training and toolbox talks;",
  ];

  safetyReq.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "The Contractor shall be solely responsible for maintaining a safe work environment and shall indemnify SAAM against any labour or safety related claims.",
    15,
    y,
    155
  );

  y += 6;
  doc.text("B. Environmental Standards", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  const environmental = [
    "Maintain water purity;",
    "Protect soil and biodiversity;",
    "Manage waste responsibly;",
    "Control pollution;",
    "Minimize emissions;",
    "Maintain environmental procedures and safety measures at site.",
  ];

  environmental.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "All products, materials, services and execution activities delivered by the Contractor shall comply with environmental, safety and quality criteria specified under this Letter of Award, applicable laws and instructions issued by SAAM / PMC.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("C. Business Ethics", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "Business must be conducted with integrity, transparency, fairness and good faith. The Contractor must not offer, accept, demand, promise or facilitate:",
    15,
    y,
    155
  );

  y += 4;

  const ethics = [
    "Payments;",
    "Gifts;",
    "Favours;",
    "Bribes;",
    "Entertainment;",
    "Advantages;",
    "Commission, kickback or illegal gratification;",
    "Any benefit intended to influence decisions involving SAAM or its officials.",
  ];

  ethics.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 26);
}

function addPage26Continuation(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "SAAM similarly shall not offer any such benefit to the Contractor. Any violation of this clause shall constitute material breach of contract.",
    15,
    y,
    155
  );

  y += 4;

  doc.setFont("times", "bold");
  y = addWrappedText(doc, "Any violation shall lead to:", 15, y, 155);
  y += 4;

  const violation = [
    "Immediate termination;",
    "Legal action;",
    "Blacklisting / debarment across all SAAM projects;",
    "Forfeiture / recovery from Security Deposit, EMD, RA Bills or Final Bill;",
    "Reporting to competent authority wherever required.",
  ];

  doc.setFont("times", "normal");

  violation.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  addFooter(doc, 26);
}

function addPage27(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "There shall be full respect for internationally recognized human rights standards and the Contractor shall ensure that:",
    15,
    y,
    155
  );

  y += 4;

  doc.setFont("times", "normal");

  const humanRights = [
    "They are not complicit in any form of human rights violation;",
    "Discrimination against employees, workers or staff is strictly prohibited;",
    "Violence, harassment, forced labour and discrimination in employment are strictly prohibited;",
    "Harassment of any kind in recruitment, promotion, deployment or termination shall not be tolerated;",
    "All workers shall be treated with dignity, fairness and lawful working conditions.",
  ];

  humanRights.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "This includes discrimination on the basis of gender, ethnic origin, religion, skin colour, age, disability or any other prohibited ground under applicable law.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("D. Concluding Comments", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "SAAM’s supervising officials shall monitor enforcement of this Contractor Code of Conduct, and the Contractor shall exercise continuous diligence to ensure these principles are strictly maintained by its employees, vendors, labour contractors and supply-chain partners.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "Safeguarding the above standards is a continuous compliance and improvement process. SAAM may periodically review, update and enforce these principles as required.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("Acceptance of Letter of Award", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor may record acceptance of this Letter of Award by signing and stamping the document and uploading the accepted copy through the SAAM Tender Management Portal.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "Alternatively, the signed and stamped acceptance copy may be submitted to the official email address notified by SAAM.",
    15,
    y,
    155
  );

  y += 6;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "Official Email: tender@shineagri.org",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "Any acceptance uploaded on the SAAM Tender Management Portal shall be treated as valid, final and legally binding acceptance of this Letter of Award.",
    15,
    y,
    155
  );

  addFooter(doc, 27);
}
function addProtectionClause(doc, number, title, text, y) {
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  y = addWrappedText(doc, `${number}. ${title}`, 15, y, 178, 5);
  y += 3;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  y = addWrappedText(doc, text, 15, y, 178, 5);
  y += 6;

  addSectionLine(doc, y);
  return y + 7;
}

function addPagePMCProtection1(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.text("ADDITIONAL SPECIAL CONDITIONS - PMC PROTECTION", 105, y, {
    align: "center",
  });
  y += 10;

  y = addProtectionClause(
    doc,
    55,
    "Capacity and Independent Contractor",
    `${data.pmcName} issues this LOA in its disclosed capacity as Project Management Consultant and authorised implementing, supervisory, monitoring and certifying authority for ${data.pacsName}. The Contractor is engaged as an independent turnkey contractor and has no authority to bind the PMC, PACS, NAFED, any bank, financing institution or government authority. No partnership, employment, joint venture, fiduciary relationship or undisclosed agency is created by this LOA.`,
    y
  );

  y = addProtectionClause(
    doc,
    56,
    "Single-Point Turnkey Responsibility",
    "The Contractor accepts complete and single-point responsibility for site verification, setting-out, temporary works, labour, supervision, plant, materials, fabrication, transport, unloading, storage, construction, testing, commissioning, statutory compliance, rectification, as-built records and safe handover of the complete functional facility. Review, inspection, measurement, approval or certification by the PMC shall not release or reduce the Contractor's responsibility for design interfaces, workmanship, quality, safety, quantities, coordination or compliance.",
    y
  );

  y = addProtectionClause(
    doc,
    57,
    "No Oral Instruction or Unauthorised Variation",
    "No extra, substituted, deviated or omitted work shall create any right to additional payment or extension unless the scope, rate and time effect are approved before execution through a written change order signed by the authorised PMC signatory. Site discussions, telephone calls, messages, unsigned drawings, engineer presence, measurement, certification, use or benefit of work shall not by themselves constitute approval, acceptance, amendment or waiver. The Contractor shall promptly seek written clarification before executing any ambiguous or conflicting requirement.",
    y
  );

  addFooter(doc);
}

function addPagePMCProtection2(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.text("ADDITIONAL SPECIAL CONDITIONS - COMMERCIAL RISK", 105, y, {
    align: "center",
  });
  y += 10;

  y = addProtectionClause(
    doc,
    58,
    "Project Funds and No Personal Liability",
    "The Contractor acknowledges that certified project payments are administered against funds released for this Project by the beneficiary/PACS, bank, financing institution or scheme authority. Unless the PMC expressly assumes a separate funding obligation in a written instrument signed by its authorised signatory, the PMC is not required to advance its own funds. No director, office-bearer, employee, engineer or consultant of the PMC incurs personal liability. This clause is subject to mandatory and non-waivable statutory payment rights, and does not validate an uncertified, disputed, defective or unsupported claim.",
    y
  );

  y = addProtectionClause(
    doc,
    59,
    "Contemporary Notice and Substantiation of Claims",
    `Any event alleged to affect time, cost, quantity or performance must be notified to the PMC in writing within ${data.claimNoticeDays} days of occurrence, with the contractual basis, dates, affected activities, mitigation steps and available records. Full calculations and supporting documents shall follow promptly. Failure to give timely notice may result in rejection to the extent the delay prevents contemporaneous verification, causation review or mitigation. No claim is admitted merely because it is included in an RA Bill or final bill.`,
    y
  );

  y = addProtectionClause(
    doc,
    60,
    "Reasonable Liquidated Damages",
    `For culpable delay beyond the contractual completion date, the PMC may levy reasonable liquidated damages at ${data.liquidatedDamagesPerWeekPercent}% of the accepted contract value per completed week or part thereof, subject to a maximum of ${data.liquidatedDamagesCapPercent}% of the accepted contract value. The parties record this as a genuine pre-estimate of likely project and administrative loss, subject to applicable law. Levy of LD does not excuse completion or rectification and does not permit double recovery for the same loss.`,
    y
  );

  addFooter(doc);
}

function addPagePMCProtection3(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.text("ADDITIONAL SPECIAL CONDITIONS - DEFAULT & RECOVERY", 105, y, {
    align: "center",
  });
  y += 10;

  y = addProtectionClause(
    doc,
    61,
    "Suspension, Step-In and Risk-and-Cost Completion",
    `For material breach, slow progress, defective work or statutory/safety non-compliance, the PMC may issue a cure notice of ${data.defaultCurePeriodDays} days. If the breach is not cured, the PMC may suspend the affected work, take protective control of project materials and records, engage another agency, terminate the whole or part and recover the reasonable excess completion, protection, testing and rectification cost. Immediate protective action or termination may be taken for abandonment, fraud, bribery, forged records, serious safety danger, insolvency or unlawful conduct, subject to applicable law.`,
    y
  );

  y = addProtectionClause(
    doc,
    62,
    "Set-Off, No Lien, Assignment and Subcontracting",
    "After giving written particulars, the PMC may set off and recover amounts lawfully due from EMD, Performance Security, retention, RA Bills, final bill or any other amount payable under this Project, without preventing recovery of any lawful balance. The Contractor shall not create any lien, charge or third-party right over the site, materials, work or project documents. The Contract shall not be assigned, and no substantial or specialised work shall be subcontracted, without prior written PMC consent. Consent shall not release the Contractor from any responsibility.",
    y
  );

  y = addProtectionClause(
    doc,
    63,
    "Force Majeure and Mitigation",
    "Force majeure is limited to an unforeseeable event beyond reasonable control that directly prevents performance despite reasonable mitigation. The affected party shall notify the PMC within three days, provide continuing evidence, protect the works and resume promptly. Shortage of funds, ordinary price increase, normal seasonal rain, labour or subcontractor default, machinery breakdown or procurement delay is not force majeure unless directly caused by a qualifying event. Relief shall ordinarily be extension of time only; cost is payable only through an express written change order.",
    y
  );

  addFooter(doc);
}

function addPagePMCProtection4(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.text("ADDITIONAL SPECIAL CONDITIONS - DISPUTES & RECORDS", 105, y, {
    align: "center",
  });
  y += 10;

  y = addProtectionClause(
    doc,
    64,
    "Claims Escalation and Continued Performance",
    "A dispute or claim shall first be submitted to the PMC's authorised representative with facts, contractual basis, calculations and supporting records. Senior representatives of both parties shall attempt resolution within 15 days. Pending resolution, the Contractor shall continue all undisputed and safe work and both parties shall perform undisputed obligations, unless the Contract is lawfully terminated or performance is impossible. Silence, negotiation or part-payment shall not amount to admission of a disputed claim.",
    y
  );

  y = addProtectionClause(
    doc,
    65,
    "Arbitration, Seat and Supervisory Courts",
    `Any unresolved dispute shall be referred to a sole arbitrator mutually appointed by the parties. If the parties do not agree on the appointment, either party may seek appointment in accordance with the Arbitration and Conciliation Act, 1996. The juridical seat and venue shall be ${data.jurisdiction}; proceedings shall be in English and the award shall state reasons. Courts having jurisdiction over the stated seat shall have exclusive supervisory jurisdiction, subject to mandatory law.`,
    y
  );

  y = addProtectionClause(
    doc,
    66,
    "Electronic Acceptance and Audit Trail",
    "Portal issuance, authenticated login, signed upload, valid electronic/digital signature, verified email acknowledgement and system audit logs may be relied upon as electronic records. Mere internal generation of the LOA without dispatch or portal availability does not by itself prove Contractor acceptance. The acceptance event, accepting user, timestamp, document hash/version and uploaded signed copy shall be preserved in the project record.",
    y
  );

  y = addProtectionClause(
    doc,
    67,
    "No Waiver, Severability and Survival",
    "Amendment or waiver is valid only when recorded in writing by an authorised signatory. Delay, inspection, certification, payment or partial exercise of a right is not a waiver. If any provision is held unenforceable, it shall be read down or severed only to the minimum extent and the remaining provisions shall continue. Accrued payment and recovery rights, audit, confidentiality, indemnity, warranty, latent-defect, fraud and dispute provisions survive completion, cancellation, expiry or termination.",
    y
  );

  addFooter(doc);
}

function addPage28(doc, data) {
  addPageBorder(doc);
  let y = 35;

  doc.setLineWidth(0.3);
  doc.line(15, y, 195, y);
  y += 22;

  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.text("Welcome Onboard!", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(11);
  y = addWrappedText(
    doc,
    "We look forward to a productive, transparent, and long-term association with you.",
    15,
    y,
    155,
    6
  );

  y += 12;
  doc.line(15, y, 195, y);
  y += 18;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("Best Regards,", 15, y);
  y += 18;

  // Optional signature image if you pass base64/image data as data.signatureImage
  if (data.signatureImage) {
    try {
      doc.addImage(data.signatureImage, "PNG", 15, y - 5, 65, 28);
      y += 32;
    } catch (e) {
      doc.setFont("times", "italic");
      doc.text("[Digital Signature / Stamp]", 15, y);
      y += 14;
    }
  } else {
    doc.setFont("times", "italic");
    doc.text("[Digital Signature / Stamp]", 15, y);
    y += 14;
  }

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("Authorised Signatory", 15, y);
  y += 5;

  y = addWrappedText(
    doc,
    "Shine Agri Allied & Marketing Multistate Cooperative Society Limited (SAAM)",
    15,
    y,
    155,
    6
  );

  y += 3;

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  y = addWrappedText(
    doc,
    "Head Office: 48, First Amar Arcade, Nilpur, Taluka Dantiwada, District Banaskantha, Gujarat – 385505.",
    15,
    y,
    155,
    5
  );

  y += 18;

  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.text("Contractor Acceptance:", 15, y);
  y += 5;

  doc.setFont("times", "normal");
  doc.text("Name: ________________________________", 15, y);
  y += 5;
  doc.text("Designation: __________________________", 15, y);
  y += 5;
  doc.text("Signature & Seal: _____________________", 15, y);
  y += 5;
  doc.text("Date: _________________________________", 15, y);

  addFooter(doc, 28);
}
function addPage29(doc, data) {
  addPageBorder(doc);

  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.text("ANNEXURE–III", 105, y, { align: "center" });

  y += 6;

  doc.setFontSize(12);
  doc.text("LIST OF MANDATORY TESTS", 105, y, { align: "center" });

  y += 6;

  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    theme: "grid",
    head: [
      [
        "Sl. No.",
        "Description of Material",
        "Test",
        "Reference of IS Code / Specification",
        "Field / Laboratory Test",
        "Frequency of Testing",
      ],
    ],
    body: [
      ["1", "Cube Test", "", "", "", ""],
      [
        "(i)",
        "Reinforced Cement Concrete – Building Works",
        "7-day & 28-day Compressive Strength",
        "IS 516",
        "Lab",
        "One sample of 6 cubes for every 50 cum or part thereof",
      ],
      [
        "(ii)",
        "Slump Test",
        "As per Appendix ‘D’, Chapter 4, CPWD Specifications",
        "",
        "Field",
        "Minimum one test for every 20 cum of concrete or part thereof",
      ],
      ["2", "Wet Mix Macadam (WMM)", "", "", "", ""],
      [
        "(i)",
        "Maximum Dry Density / OMC",
        "",
        "IS 2720-VIII",
        "Lab",
        "Minimum 1 test per source; additional tests as required by SAAM Engineer-In-Charge",
      ],
      [
        "(ii)",
        "Combined Flakiness & Elongation Indices",
        "",
        "IS 2386-I",
        "Lab",
        "Minimum 1 test per source; additional test after every 500 cum",
      ],
      [
        "(iii)",
        "Moisture Content",
        "",
        "IS 2720-II",
        "Field",
        "Minimum 3 tests per day",
      ],
      [
        "(iv)",
        "Field Density",
        "",
        "IS 2720-XXVII",
        "Field",
        "One set of 3 tests per 2000 sqm or part thereof",
      ],
      ["3", "Brick Work / Sewer Bricks / Burnt Clay Perforated Building Bricks", "", "", "", ""],
      [
        "(i)",
        "Dimension Test",
        "Appendix A, B & C of Chapter 6, CPWD Specification",
        "",
        "Lab",
        "Minimum one test for every 50,000 bricks or part thereof",
      ],
    ],
    styles: {
      font: "times",
      fontSize: 8,
      cellPadding: 2,
      valign: "middle",
      overflow: "linebreak",
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 13, halign: "center" },
      1: { cellWidth: 36 },
      2: { cellWidth: 32 },
      3: { cellWidth: 35 },
      4: { cellWidth: 40 },
      5: { cellWidth: 36 },
    },
    didParseCell: function (hookData) {
      const row = hookData.row.raw;
      if (row && ["1", "2", "3"].includes(row[0])) {
        hookData.cell.styles.fontStyle = "bold";
        hookData.cell.styles.fillColor = [250, 250, 250];
      }
    },
  });

  addFooter(doc, 29);
}
function addPage30(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.text("ANNEXURE–IV", 105, y, { align: "center" });

  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(12);
  doc.text("MATERIAL TESTS", 105, y, { align: "center" });

  y += 18;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("1. For All Civil & Electrical Works", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "All mandatory tests specified in the following shall be strictly followed:",
    15,
    y,
    155
  );

  y += 4;

  const testSources = [
    "The Contract;",
    "Technical Specifications;",
    "IS Codes;",
    "CPWD Specifications wherever applicable;",
    "Directions issued by SAAM Engineer-In-Charge / PMC.",
  ];

  testSources.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Testing charges shall be borne entirely by the Contractor. No extra payment shall be made for mandatory testing, sampling, transportation of samples or retesting due to failure.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("2. For Pre-Engineered Steel System (PEB)", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(
    doc,
    "The Contractor must conduct necessary laboratory tests for all PEB components including:",
    15,
    y,
    155
  );

  y += 4;

  const pebItems = [
    "Primary steel members;",
    "Secondary steel members;",
    "Roofing sheets;",
    "Wall cladding sheets;",
    "Accessories;",
    "Bolts, fasteners, anchor bolts, welding materials and protective coatings wherever applicable.",
  ];

  pebItems.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  doc.setFont("times", "bold");
  doc.text("Sampling Requirements:", 15, y);
  y += 5;

  doc.setFont("times", "normal");

  y = addWrappedText(
    doc,
    "Samples must be drawn from:",
    25,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "(a) Contractor’s fabrication workshop before dispatch;",
    35,
    y,
    145
  );

  y += 5;

  y = addWrappedText(
    doc,
    "(b) Site after receipt of material.",
    35,
    y,
    145
  );

  y += 5;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "All samples must be tested in SAAM-approved laboratories only.",
    15,
    y,
    155
  );

  addFooter(doc, 30);
}
function addPage31(doc, data) {
  addPageBorder(doc);
  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.text("Cost Responsibility:", 15, y);
  y += 5;

  doc.setFont("times", "normal");

  const costContinue = [
    "Cost of samples;",
    "Cost of transportation to test laboratory;",
    "Testing charges;",
    "Retesting charges, if any;",
    "Replacement cost of rejected material.",
  ];

  costContinue.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 6;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "All such costs shall be borne exclusively by the Contractor.",
    15,
    y,
    155
  );

  y += 6;
  addSectionLine(doc, y);
  y += 6;

  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("3. Manufacturer’s Quality Certificates", 15, y);
  y += 6;

  doc.setFont("times", "normal");
  doc.setFontSize(10);

  y = addWrappedText(doc, "The Contractor must submit:", 15, y, 155);
  y += 4;

  const certificates = [
    "Manufacturer’s Test Certificates;",
    "Material Compliance Certificates;",
    "Chemical and Physical Test Certificates;",
    "Batch-wise quality certificates;",
    "Warranty certificates wherever applicable.",
  ];

  certificates.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 5;

  y = addWrappedText(
    doc,
    "The above certificates shall be submitted for every batch of material supplied and shall be uploaded on the SAAM Tender Management Portal wherever required.",
    15,
    y,
    155
  );

  y += 5;

  doc.setFont("times", "bold");
  doc.text("If:", 15, y);
  y += 5;

  doc.setFont("times", "normal");

  const defects = [
    "Certificates are missing;",
    "Certificates are incomplete;",
    "Material differs from standard specifications;",
    "Material does not match approved make / brand / quality;",
    "Test results are unsatisfactory or doubtful.",
  ];

  defects.forEach((item) => {
    y = addBullet(doc, item, 25, y, 155);
    y += 3;
  });

  y += 6;

  doc.setFont("times", "bold");
  y = addWrappedText(
    doc,
    "Such material shall not be allowed for construction and shall be removed / replaced by the Contractor at its own cost and risk.",
    15,
    y,
    155
  );

  y += 6;

  y = addWrappedText(
    doc,
    "SAAM / PMC may withhold payment, reject material, order retesting or recover losses if non-compliant material is supplied or used.",
    15,
    y,
    155
  );

  addFooter(doc, 31);
}
function addPage32(doc, data) {
  addPageBorder(doc);

  let y = 22;

  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.text("ANNEXURE–V", 105, y, { align: "center" });

  y += 5;

  doc.setFont("times", "normal");
  doc.setFontSize(12);
  doc.text("LIST OF APPROVED MAKES", 105, y, { align: "center" });

  y += 5;

  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    theme: "grid",
    head: [["Sr. No.", "Name of Material", "Approved Make"]],
    body: [
      ["1(a)", "Cement (Portland Pozzolana Cement – PPC)", "ACC, UltraTech, J.P. Rewa, Vikram, Shree Cement, Lafarge, Mycem Cement, Birla Cement, Prism, Ambuja Cement & Cement Company of India, JK Laxmi, Dalmia OR any reputed manufacturer of major plants approved by SAAM Engineer-In-Charge, confirming to latest IS specifications."],
      ["1(b)", "Cement (Ordinary Portland Cement – OPC)", "Gujarat Ambuja, ACC, JK, Vikram, Shree, Ultratech, Birla Uttam, Binani, Lafarge, Madras Cement (Ramco), India Cement, Jaypee, Dalmia OR approved major manufacturers as certified by SAAM Engineer-In-Charge, confirming to latest IS specifications."],
      ["2", "Reinforcement Steel", "TATA, SAIL, RINL, JINDAL OR BIS-approved license holder / manufacturer."],
      ["3", "Ceramic Glazed Tile", "Kajaria, Johnson, Somany (1st quality) OR equivalent."],
      ["4", "Vitrified Floor Tile", "Kajaria, Johnson, Somany (1st quality) OR equivalent (minimum 10 mm thickness & Grade-V)."],
      ["5", "PVC Drain Pipe", "Supreme, Prakash, Kissan (minimum 6.0 kgf/cm² pressure) OR as per SAAM Engineer’s instructions."],
      ["6", "CPVC Water Supply Pipe", "Astral, Supreme, Prakash & Prince (wall pressure minimum 500 psi)."],
      ["7", "G.I. Pipes", "Jindal (Hisar), Prakash Surya, Tata OR equivalent."],
      ["8", "W.C.", "Roca, Kohler, Hindware."],
      ["9", "Wash Basin", "Roca, Kohler, Hindware."],
      ["10", "Urinal", "Roca, Kohler, Hindware."],
      ["11", "C.P. Fitting", "Grohe, Kohler, Hindware."],
      ["12", "C.I. Pipe & Fittings", "SKF, Electrosteel."],
      ["13", "Aluminium Frame", "Jindal, Hindalco."],
      ["14", "Gypsum Board Ceiling", "Channels & boards of India Gypsum / Lafarge."],
      ["15", "Grid Ceiling", "Armstrong make (ULTIMA regular panel & Silhouette XI 1/8 inch Reveal) OR equivalent."],
      ["16", "Acrylic Paint", "Asian Paints, ICI (Dulux), Nerolac, Berger, Johnson & Nicholson."],
      ["17", "Stainless Steel", "Jindal, SAIL, Indian Stainless Steel."],
      ["18", "G.I. Fittings", "UNIK, ZOLOTO, NVR OR equivalent."],
      ["19", "Structural Steel", "TATA, SAIL, Jindal, RINL, IISCO OR any manufacturer having BIS-approved license."],
      ["20", "Structural Steel – MS Pipes Only", "TATA, Jindal OR any BIS-approved manufacturer."],
      ["21", "Chequered Precast Concrete Tile", "Hindustan Tiles, Ultra, Swastik Tiles."],
      ["22", "PVC Casing Pipe", "Kissan (Agro), Appollo, Supreme."],
      ["23", "Ply Board & Ply", "Green Ply, Kitply, Century."],
      ["24", "Galvalume Sheets", "TATA Bluescope, JSW Colouron, JSW Steel."],
      ["25", "Self Drilling / Tapping Zinc-Plated Screws (5.5 x 55 mm) for Galvalume Sheets", "HILTI OR equivalent."],
    ],
    styles: {
      font: "times",
      fontSize: 7.2,
      cellPadding: 1.6,
      valign: "middle",
      overflow: "linebreak",
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 20, halign: "center", fontStyle: "bold" },
      1: { cellWidth: 45 },
      2: { cellWidth: 117 },
    },
  });

  addFooter(doc, 32);
}
function addPage33(doc, data) {
  addPageBorder(doc);

  let y = 35;

  doc.setFont("times", "bold");
  doc.setFontSize(12);
  doc.text("NOTE", 15, y);

  y += 15;

  doc.setFont("times", "normal");
  doc.setFontSize(11);

  y = addWrappedText(
    doc,
    "All makes listed in Annexure-V are subject to meeting the technical specifications, performance requirements, quality standards and approved drawings applicable to the Project.",
    15,
    y,
    155,
    6
  );

  y += 5;

  y = addWrappedText(
    doc,
    "The Contractor shall obtain prior written approval of the specific make, brand, model or manufacturer from the SAAM Engineer-In-Charge / PMC before procurement, delivery or commencement of installation.",
    15,
    y,
    155,
    6
  );

  y += 5;

  doc.setFont("times", "bold");

  y = addWrappedText(
    doc,
    "Any material procured, delivered or installed without prior approval may be rejected and replaced by the Contractor at its own cost and risk.",
    15,
    y,
    155,
    6
  );

  y += 5;

  y = addWrappedText(
    doc,
    "The decision of the SAAM Engineer-In-Charge regarding approval, rejection, suitability, equivalency, quality, make or brand shall be final and binding upon the Contractor.",
    15,
    y,
    155,
    6
  );

  y += 5;

  doc.setFont("times", "normal");

  y = addWrappedText(
    doc,
    "No additional payment, extension of time or compensation shall be admissible on account of rejection or replacement of unapproved materials.",
    15,
    y,
    155,
    6
  );

  addFooter(doc, 33);
  }


 async function mergeBOQWithLOA(loaBlob, boqPdfUrl) {
  if (!boqPdfUrl) return loaBlob;

  const loaBytes = await loaBlob.arrayBuffer();

  const boqResponse = await fetch(boqPdfUrl);
  if (!boqResponse.ok) return loaBlob;

  const boqBytes = await boqResponse.arrayBuffer();

  const finalPdf = await PDFDocument.create();
  const loaPdf = await PDFDocument.load(loaBytes);
  const boqPdf = await PDFDocument.load(boqBytes);

  const loaPages = await finalPdf.copyPages(loaPdf, loaPdf.getPageIndices());
  loaPages.forEach((page) => finalPdf.addPage(page));

  const boqPages = await finalPdf.copyPages(boqPdf, boqPdf.getPageIndices());
  boqPages.forEach((page) => finalPdf.addPage(page));

  const finalBytes = await finalPdf.save();

  return new Blob([finalBytes], { type: "application/pdf" });
}

export default async function generateLOAPDF(data = {}) {
  data = normalizeLOAData(data);
  const doc = new jsPDF("p", "mm", "a4");

  __pageNo = 1;

  const pages = [
  addPage1,
  addPage2,
  addPage3,
  addPage4,
  addPage5,
  addPage6,
  addPage7,
  addPage8,
  addPage9,
  addPage10,
  addPage11,
  addPage12,
  addPage12Continuation,
  addPage13,
  addPage14,
  addPage15,
  addPage16,
  addPage17,
  addPage17Continuation,
  addPage18,
  addPage19,
  addPage20,
  addPage20Continuation,
  addPage21,
  addPage22,
  addPage23,
  addPage24,
  addPage25,
  addPage26,
  addPage26Continuation,
  addPage27,
  addPagePMCProtection1,
  addPagePMCProtection2,
  addPagePMCProtection3,
  addPagePMCProtection4,
  addPage28,
  addPage29,
  addPage30,
  addPage31,
  addPage32,
  addPage33,
];

pages.forEach((pageFn, index) => {
  pageFn(doc, data);

  if (index !== pages.length - 1) {
    doc.addPage();
    __pageNo++;
  }
});
  const loaBlob = doc.output("blob");
  return await mergeBOQWithLOA(loaBlob, data.boqPdfUrl);
}
